import { useCallback, useEffect, useRef, type FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import {
  selectPlaylistById,
  selectPlaylists,
  selectPlaylistsError,
  selectPlaylistsIsLoading,
  selectSelectedPlaylistIds,
} from "@ds/modules/content/playlists/redux/selectors";
import { playlistsActions } from "@ds/modules/content/playlists/redux/slice";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-width: 400px;
`;

export const DuplicatePlaylistModal: FC<{ onClose: () => void; playlistId?: number; onSuccess?: () => void }> = ({
  onClose,
  playlistId: playlistIdProp,
  onSuccess,
}) => {
  const selectedPlaylistId = useRootSelector(selectSelectedPlaylistIds)[0];
  const playlistId = playlistIdProp ?? selectedPlaylistId;
  const playlist = useRootSelector(state => selectPlaylistById(state, playlistId));
  const playlists = useRootSelector(selectPlaylists);
  const playlistNames = playlists.map(_playlist => _playlist.name.trim().toLowerCase());
  const isLoading = useRootSelector(selectPlaylistsIsLoading);
  const error = useRootSelector(selectPlaylistsError);
  const hasSubmitted = useRef(false);
  const dispatch = useRootDispatch();

  useEffect(() => {
    if (hasSubmitted.current && !isLoading) {
      if (!error) {
        onSuccess?.();
      }
      onClose();
    }
  }, [isLoading, error, onClose, onSuccess]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: playlist?.name ? `${playlist.name} (Copy)` : "",
    },
  });

  const onSubmit = useCallback(
    (data: { name: string }) => {
      if (playlistId === undefined || playlistId === null) return;
      hasSubmitted.current = true;
      dispatch(playlistsActions.duplicatePlaylist({ playlistId, newTitle: data.name }));
    },
    [dispatch, playlistId],
  );

  // NOTE: Name uniqueness is validated against the currently loaded playlists in the Redux store
  // (typically one page of results). Names outside the current page are invisible to this check,
  // so duplicate names on other pages will only be caught by the API error response.
  const { ref, ...nameRegister } = register("name", {
    required: "Name is required",
    validate: value => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return true;
      const isDuplicate = playlistNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
      return isDuplicate ? "A playlist with this name already exists" : true;
    },
  });

  const renderFooter = useCallback(
    () => (
      <>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          isDisabled={isLoading}
          onClick={onClose}
        />
        <Button
          label="Duplicate"
          ariaLabel="duplicate button"
          buttonType={ButtonType.Raised}
          icon={IconType.Duplicate}
          iconPosition="right"
          isLoading={isLoading}
          isDisabled={playlistId === undefined || playlistId === null}
          onClick={handleSubmit(onSubmit)}
        />
      </>
    ),
    [playlistId, isLoading, onClose, handleSubmit, onSubmit],
  );

  return (
    <Dialog modal visible header="Duplicate Playlist" footer={renderFooter} onHide={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabelWrapper label="New Playlist Name" isRequired error={errors.name}>
          <InputText
            inputRef={ref}
            placeholder="Enter a new name for the duplicated playlist"
            {...nameRegister}
            value={watch("name") ?? ""}
          />
        </LabelWrapper>
      </Form>
    </Dialog>
  );
};
